// Lightweight wrapper around face-api.js (TinyFaceDetector) used by the
// points-watching camera to decide whether the captured frame actually
// contains a clear face. Runs entirely in the browser — no image leaves the
// device for this check.
//
// Model files must live in `public/models/` (served at `/models`):
//   - tiny_face_detector_model-weights_manifest.json
//   - tiny_face_detector_model-shard1
//   - ssd_mobilenetv1_model-weights_manifest.json   (fallback pass, lazy)
//   - ssd_mobilenetv1_model-shard1 / -shard2
// Grab them from the face-api.js repo (weights/ folder). Until the tiny files
// are present detection is a safe no-op: detectFace() resolves to null and the
// server falls back to its previous "photo present = OK" behavior (no false
// alerts). The SSD files are optional — if they're missing the fallback pass
// is skipped and the tiny verdict stands.

import * as faceapi from 'face-api.js';

const MODEL_URL = '/models';

// A face counts as "proper" either when the detector is reasonably confident
// AND the face is big enough relative to the frame, OR when the face box is so
// large that its presence is unambiguous regardless of confidence.
//
// SCORE_THRESHOLD lowered from 0.5: under the heavy colored gameroom lighting
// even clear, properly sized faces only score ~0.2–0.25 (measured on real
// captures), so a 0.5 gate false-flagged them as "no clear image". The width
// gate still rejects tiny background faces / noise at this lower score.
const SCORE_THRESHOLD = 0.2;
const MIN_FACE_WIDTH_RATIO = 0.12; // confident path: box width >= 12% of frame width
// Large-box override: a face box this wide is unmistakably the subject, so we
// accept it even when the detector's confidence is low (as long as it cleared
// the detector floor below). 0.30 ≈ a normal points-watching close-up.
const LARGE_FACE_WIDTH_RATIO = 0.3;
// Floor passed to the detector itself. detectAllFaces() discards anything below
// this, so it must sit under the low scores real faces get here — otherwise the
// large-box override never sees the candidate detection, and the confident-path
// SCORE_THRESHOLD above (0.2) does the real quality filtering.
const DETECT_SCORE_FLOOR = 0.1;
// A larger input size than the 320 default helps recall on these frames.
const INPUT_SIZE = 416;

// Verdicts sent to the server alongside the boolean:
//   CLEAR    - a proper face passed the accept gates
//   UNCLEAR  - face candidates were found but none passed the gates, i.e. a
//              person is likely present but the capture isn't clear
//   NO_HUMAN - no face candidate of meaningful size in any pass
export const FACE_VERDICT = {
  CLEAR: 'CLEAR',
  UNCLEAR: 'UNCLEAR',
  NO_HUMAN: 'NO_HUMAN',
};

// A rejected candidate still counts as "a human is probably there" when its
// box has meaningful size. Measured on real captures: noise boxes (machine
// screens, reflections) stay under ~3% of frame width, while even small real
// faces come in at ~8-11% — so 8% separates them.
const HUMAN_EVIDENCE_WIDTH_RATIO = 0.08;
// ...but size alone isn't enough: an empty-room capture produced a 12%-wide
// jacket-on-a-chair box at score 0.102 that false-flagged "Person detected".
// Real (if unclear) faces measure >= 0.2, so 0.13 sits between them and gates
// what counts as evidence without touching genuine unclear captures.
const EVIDENCE_SCORE_THRESHOLD = 0.13;

// Upper bound on a believable face box. SSD emits garbage boxes spanning the
// whole frame on empty scenes (measured: 0.51 score at 98% frame width on a
// shot of empty chairs) which the large-box override would happily accept.
// A real face never fills the frame on these wall cameras — the largest
// genuine capture measured (extreme close-up of a head) was 49% — so boxes
// wider than 70% are junk, for both accepting and human-evidence purposes.
const MAX_FACE_WIDTH_RATIO = 0.7;

// Huge-face recovery pass (pass 4). An extreme close-up tilted down at the
// machine (head >= ~50% of frame width) defeats every regular pass outright —
// zero candidates — because the face is far larger than the detectors' sweet
// spot. Shrinking the face relative to the detector input brings it back:
// tiny@320 on the stretched frame scored 0.095 at 49% width on a real flagged
// capture, and tiny@416 on a half-scale grayscale scored 0.066 at 47%. Junk
// boxes on the same passes stay under 16% width, so a 30%-width floor (the
// existing large-box ratio) cleanly separates them even at these low scores.
const HUGE_INPUT_SIZE = 320;
const HUGE_DETECT_SCORE_FLOOR = 0.06;
const HALF_SCALE = 0.5;

// SSD MobileNet V1 fallback pass. TinyFaceDetector misses non-frontal poses
// (e.g. a large face tilted down toward the machine) that SSD finds easily.
// Thresholds measured on real flagged captures: a downward-tilted subject face
// scored 0.37 at 28% frame width, while junk boxes (machine screens, small
// background faces) stayed under 0.24 or under 10% width — so 0.3 + the same
// width gates separate them cleanly. SSD runs on the raw color frame only:
// grayscale/stretched variants LOWER its scores (it was trained on color).
const SSD_DETECT_SCORE_FLOOR = 0.2;
const SSD_SCORE_THRESHOLD = 0.3;

let modelsPromise = null;
let modelsReady = false;
let ssdPromise = null;
let ssdReady = false;

// Loads the TinyFaceDetector model once. Resolves true on success, false if
// the model files can't be loaded (missing/offline) so callers can degrade
// gracefully instead of throwing.
export function loadFaceModels() {
  if (modelsReady) return Promise.resolve(true);
  if (!modelsPromise) {
    modelsPromise = faceapi.nets.tinyFaceDetector
      .loadFromUri(MODEL_URL)
      .then(() => {
        modelsReady = true;
        return true;
      })
      .catch((err) => {
        // Reset so a later attempt (e.g. after files are deployed) can retry.
        modelsPromise = null;
        console.error('face-api.js models failed to load from', MODEL_URL, err);
        return false;
      });
  }
  return modelsPromise;
}

// Loads the SSD MobileNet V1 model on first use only (~5.4MB — too heavy to
// fetch at app start, and it's only needed for the rare frame the tiny passes
// can't resolve). Resolves true/false like loadFaceModels().
function loadSsdModel() {
  if (ssdReady) return Promise.resolve(true);
  if (!ssdPromise) {
    ssdPromise = faceapi.nets.ssdMobilenetv1
      .loadFromUri(MODEL_URL)
      .then(() => {
        ssdReady = true;
        return true;
      })
      .catch((err) => {
        ssdPromise = null;
        console.error(
          'face-api.js SSD model failed to load from',
          MODEL_URL,
          err
        );
        return false;
      });
  }
  return ssdPromise;
}

// Detects whether `input` (an HTMLCanvasElement / HTMLVideoElement / HTMLImageElement)
// contains a clear face.
//   returns { faceDetected: true,  verdict: 'CLEAR' }    -> proper face found
//   returns { faceDetected: false, verdict: 'UNCLEAR' }  -> person likely
//              present (sizeable candidate) but no clear face
//   returns { faceDetected: false, verdict: 'NO_HUMAN' } -> no face evidence
//   returns null -> detection couldn't run (models missing or error); caller
//                   should treat this as "unknown" and not raise a false alert
//
// The gameroom's purple/blue lighting defeats TinyFaceDetector two different
// ways, and no single preprocessing fixes both (measured on real captures):
//   1. Brighter frames detect on a plain luminance grayscale, but break under
//      an aggressive contrast stretch (bright backgrounds blow out the face).
//   2. Very dark / deeply tinted frames vanish under luminance grayscale (the
//      0.114 blue weight crushes the blue-lit facial detail) but recover
//      strongly on a max-channel + contrast-stretched copy.
// So we run up to four passes and accept as soon as ANY finds a face; the
// later, more expensive passes only run when the earlier ones turn up nothing:
//   1. tiny on a luminance grayscale        (typical frames)
//   2. tiny on a max-channel stretch        (dark / deeply tinted frames)
//   3. tiny with the face scaled down       (extreme close-ups, head filling
//      the frame — see the huge-face constants)
//   4. SSD on the raw color frame           (non-frontal poses)
export async function detectFace(input) {
  try {
    const ok = await loadFaceModels();
    if (!ok) return null;

    const imgWidth =
      input.width || input.videoWidth || input.naturalWidth || 640;
    const clear = { faceDetected: true, verdict: FACE_VERDICT.CLEAR };
    // Any sizeable-but-rejected candidate across the passes downgrades the
    // failure verdict from NO_HUMAN to UNCLEAR.
    let humanEvidence = false;

    // Pass 1 — plain luminance grayscale (neutralizes a mild color cast).
    const gray = toGrayscaleCanvas(input);
    const grayDets = await runTinyPass(gray || input);
    if (acceptDetections(grayDets, imgWidth, SCORE_THRESHOLD)) return clear;
    humanEvidence = humanEvidence || hasHumanEvidence(grayDets, imgWidth);

    // Pass 2 — max-channel + percentile contrast stretch. Recovers the dark,
    // heavily-tinted frames that pass 1 can't see.
    const stretched = toMaxChannelStretchedCanvas(input);
    if (stretched) {
      const stretchDets = await runTinyPass(stretched);
      if (acceptDetections(stretchDets, imgWidth, SCORE_THRESHOLD)) {
        return clear;
      }
      humanEvidence = humanEvidence || hasHumanEvidence(stretchDets, imgWidth);
    }

    // Pass 3 — huge-face recovery. Re-runs the (already loaded) tiny detector
    // with the face shrunk relative to the detector input: smaller inputSize
    // on the stretched frame, plus full inputSize on a half-scale grayscale.
    // Accepts only boxes in the 30–70%-of-frame-width window — the signature
    // of an extreme close-up — so its low score floor can't admit small junk.
    // These low-floor detections deliberately do NOT feed humanEvidence.
    if (stretched) {
      const hugeDets = await runTinyPass(stretched, HUGE_INPUT_SIZE);
      if (acceptHugeFace(hugeDets, imgWidth)) return clear;
    }
    const halfGray = toDownscaledCanvas(gray || input, HALF_SCALE);
    if (halfGray) {
      const halfDets = await runTinyPass(halfGray);
      if (acceptHugeFace(halfDets, halfGray.width)) return clear;
    }

    // Pass 4 — SSD MobileNet V1 fallback on the raw color frame. Catches the
    // non-frontal poses (head tilted down at the machine) that defeat
    // TinyFaceDetector regardless of preprocessing. Only reached on frames
    // that would otherwise be flagged, so the model loads lazily here.
    if (await loadSsdModel()) {
      const ssdDets = await faceapi.detectAllFaces(
        input,
        new faceapi.SsdMobilenetv1Options({
          minConfidence: SSD_DETECT_SCORE_FLOOR,
        })
      );
      if (acceptDetections(ssdDets, imgWidth, SSD_SCORE_THRESHOLD)) {
        return clear;
      }
      humanEvidence = humanEvidence || hasHumanEvidence(ssdDets, imgWidth);
    }

    return {
      faceDetected: false,
      verdict: humanEvidence ? FACE_VERDICT.UNCLEAR : FACE_VERDICT.NO_HUMAN,
    };
  } catch (err) {
    console.error('face detection error', err);
    return null;
  }
}

// Runs TinyFaceDetector on a prepared `target` canvas/element and returns the
// raw detections (accept logic is applied by the caller). The huge-face
// recovery pass overrides the input size and score floor.
function runTinyPass(target, inputSize = INPUT_SIZE) {
  const options = new faceapi.TinyFaceDetectorOptions({
    inputSize,
    scoreThreshold:
      inputSize === INPUT_SIZE ? DETECT_SCORE_FLOOR : HUGE_DETECT_SCORE_FLOOR,
  });
  return faceapi.detectAllFaces(target, options);
}

// True when any (rejected) candidate box is big enough — but not absurdly
// big — AND scored above the evidence floor, meaning "a person is probably
// in frame". Used to tell UNCLEAR apart from NO_HUMAN.
function hasHumanEvidence(detections, imgWidth) {
  if (!detections || detections.length === 0) return false;
  const evidenceWidth = imgWidth * HUMAN_EVIDENCE_WIDTH_RATIO;
  const maxWidth = imgWidth * MAX_FACE_WIDTH_RATIO;
  return detections.some(
    (d) =>
      d.box &&
      d.box.width >= evidenceWidth &&
      d.box.width <= maxWidth &&
      d.score >= EVIDENCE_SCORE_THRESHOLD
  );
}

// Shared accept logic (confident path OR large-box override), parameterized by
// score threshold since SSD scores run higher than tiny's under this lighting.
// Boxes wider than MAX_FACE_WIDTH_RATIO are junk regardless of score (see the
// constant's comment) and never accepted.
function acceptDetections(detections, imgWidth, scoreThreshold) {
  if (!detections || detections.length === 0) return false;

  const minWidth = imgWidth * MIN_FACE_WIDTH_RATIO;
  const largeWidth = imgWidth * LARGE_FACE_WIDTH_RATIO;
  const maxWidth = imgWidth * MAX_FACE_WIDTH_RATIO;

  return detections.some((d) => {
    if (!d.box || d.box.width > maxWidth) return false;
    // Confident path: reasonably sized AND high score.
    if (d.box.width >= minWidth && d.score >= scoreThreshold) return true;
    // Large-box override: a very wide face box is unambiguous even at the
    // low confidence the gameroom lighting produces.
    return d.box.width >= largeWidth;
  });
}

// Accept logic for the huge-face recovery pass: only boxes in the
// 30–70%-of-frame window count (score already floored by the detector).
function acceptHugeFace(detections, imgWidth) {
  if (!detections || detections.length === 0) return false;
  const largeWidth = imgWidth * LARGE_FACE_WIDTH_RATIO;
  const maxWidth = imgWidth * MAX_FACE_WIDTH_RATIO;
  return detections.some(
    (d) => d.box && d.box.width >= largeWidth && d.box.width <= maxWidth
  );
}

// Returns a new canvas holding `input` scaled down by `scale`, or null on
// failure so the caller can skip the pass that needed it.
function toDownscaledCanvas(input, scale) {
  try {
    const w = input.width || input.videoWidth || input.naturalWidth;
    const h = input.height || input.videoHeight || input.naturalHeight;
    if (!w || !h) return null;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext('2d').drawImage(input, 0, 0, canvas.width, canvas.height);
    return canvas;
  } catch (err) {
    return null;
  }
}

// Returns a new canvas holding the grayscale version of `input`
// (HTMLCanvasElement / HTMLVideoElement / HTMLImageElement). Returns null if a
// canvas can't be created so the caller falls back to the raw input.
function toGrayscaleCanvas(input) {
  try {
    const w = input.width || input.videoWidth || input.naturalWidth;
    const h = input.height || input.videoHeight || input.naturalHeight;
    if (!w || !h) return null;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(input, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    const px = img.data;
    for (let i = 0; i < px.length; i += 4) {
      const l = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      px[i] = px[i + 1] = px[i + 2] = l;
    }
    ctx.putImageData(img, 0, 0);
    return canvas;
  } catch (err) {
    return null;
  }
}

// Returns a new canvas where each pixel is the max of its R/G/B channels,
// then contrast-stretched so the 2nd–98th percentile maps to 0–255. Taking the
// max channel (rather than a blue-suppressing luminance) preserves the facial
// detail that the purple/blue lighting pushes into the blue channel, and the
// percentile stretch rescues badly underexposed frames. Returns null on
// failure so the caller can skip this pass.
function toMaxChannelStretchedCanvas(input) {
  try {
    const w = input.width || input.videoWidth || input.naturalWidth;
    const h = input.height || input.videoHeight || input.naturalHeight;
    if (!w || !h) return null;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(input, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    const px = img.data;
    const n = px.length / 4;

    // Per-pixel max channel.
    const m = new Uint8ClampedArray(n);
    for (let i = 0, j = 0; i < px.length; i += 4, j++) {
      let v = px[i];
      if (px[i + 1] > v) v = px[i + 1];
      if (px[i + 2] > v) v = px[i + 2];
      m[j] = v;
    }

    // Robust 2nd / 98th percentile bounds via a 256-bin histogram.
    const hist = new Int32Array(256);
    for (let j = 0; j < n; j++) hist[m[j]]++;
    const loCount = n * 0.02;
    const hiCount = n * 0.98;
    let lo = 0;
    let hi = 255;
    let acc = 0;
    for (let v = 0; v < 256; v++) {
      acc += hist[v];
      if (acc >= loCount) {
        lo = v;
        break;
      }
    }
    acc = 0;
    for (let v = 0; v < 256; v++) {
      acc += hist[v];
      if (acc >= hiCount) {
        hi = v;
        break;
      }
    }
    const range = Math.max(1, hi - lo);

    for (let i = 0, j = 0; i < px.length; i += 4, j++) {
      let s = ((m[j] - lo) / range) * 255;
      if (s < 0) s = 0;
      else if (s > 255) s = 255;
      px[i] = px[i + 1] = px[i + 2] = s;
    }
    ctx.putImageData(img, 0, 0);
    return canvas;
  } catch (err) {
    return null;
  }
}
